import React, { ReactElement, useCallback, useState } from 'react'
import Popover, { PopoverPlace } from 'react-popover'
import { useTranslation } from 'react-i18next'
import { Button, ButtonIntent } from '../../buttons/Button/Button'
import * as Styled from './styled'

type PopoverWithBtnsProps = {
  heading?: string
  onSubmit: () => void
  onCancel?: () => void
  text?: string
  cancelText?: string
  submitText?: string
  cancelIntent?: ButtonIntent
  submitIntent?: ButtonIntent
  children: ReactElement
  position?: PopoverPlace
  bodyContent?: ReactElement
  maxWidth?: string
}

const PopoverBody: React.FC<{
  heading?: string
  text?: string
  cancelText?: string
  submitText?: string
  cancelIntent?: ButtonIntent
  submitIntent?: ButtonIntent
  bodyContent?: ReactElement
  onSubmit: () => void
  onCancel: () => void
  maxWidth?: string
}> = ({
  heading,
  text,
  cancelText,
  submitText,
  cancelIntent = 'cancel',
  submitIntent = 'danger',
  bodyContent,
  onSubmit,
  onCancel,
  maxWidth,
}) => {
  const { t } = useTranslation('ui')
  return (
    <Styled.ContentWrapper maxWidth={maxWidth}>
      <Styled.Heading>{heading}</Styled.Heading>
      {text && <Styled.Text>{text}</Styled.Text>}
      {bodyContent && <Styled.BodyContentWrapper>{bodyContent}</Styled.BodyContentWrapper>}
      <Styled.ButtonsWrapper>
        <Button intent={cancelIntent} size={'small'} onClick={onCancel}>
          {cancelText || t('RemovePopover.cancel')}
        </Button>
        <Button intent={submitIntent} size={'small'} onClick={onSubmit}>
          {submitText || t('RemovePopover.submit')}
        </Button>
      </Styled.ButtonsWrapper>
    </Styled.ContentWrapper>
  )
}

export const PopoverWithBtns: React.FC<PopoverWithBtnsProps> = ({
  children,
  text,
  cancelText,
  submitText,
  cancelIntent,
  submitIntent,
  onSubmit,
  onCancel,
  position = 'start',
  heading,
  bodyContent,
  maxWidth,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const togglePopover = useCallback(() => {
    setIsOpen(!isOpen)
  }, [isOpen])
  const closePopover = useCallback(() => {
    setIsOpen(false)
    onCancel && onCancel()
  }, [onCancel])
  const handleSubmit = useCallback(() => {
    setIsOpen(false)
    onSubmit()
  }, [onSubmit])
  return (
    <Popover
      isOpen={isOpen}
      body={
        <PopoverBody
          heading={heading}
          text={text}
          cancelText={cancelText}
          submitText={submitText}
          cancelIntent={cancelIntent}
          submitIntent={submitIntent}
          onCancel={closePopover}
          onSubmit={handleSubmit}
          bodyContent={bodyContent}
          maxWidth={maxWidth}
        />
      }
      onOuterAction={closePopover}
      preferPlace={position}
      tipSize={0.01}
      style={{ zIndex: 9999 }}
    >
      {React.cloneElement(children, { onClick: togglePopover })}
    </Popover>
  )
}
